import React from 'react';
import { NextUI } from '@/components/NextUI';
import { logHubxEvent, APP_EVENTS } from '@/app/providers/analytics';

type EditProcessAnimationMode = 'compress' | 'convert' | 'merge';

type EditProcessAnimationModalData = {
  mode?: EditProcessAnimationMode;
  isComplete?: boolean;
  minDurationMs?: number;
};

const COPY_BY_MODE: Record<EditProcessAnimationMode, string> = {
  compress: 'Compressing your document...',
  convert: 'Converting your document...',
  merge: 'Merging your document...',
};

const ANALYTICS_SOURCE_BY_MODE: Record<EditProcessAnimationMode, string> = {
  compress: 'compress',
  convert: 'conversion',
  merge: 'merge',
};

const getRandomProgressIncrement = () => Math.floor(Math.random() * 4) + 2;

const getRandomProgressDelay = (isComplete: boolean) =>
  isComplete
    ? Math.floor(Math.random() * 40) + 60
    : Math.floor(Math.random() * 70) + 90;

const EditProcessAnimationModal = ({
  data,
  hideModal,
}: {
  data?: EditProcessAnimationModalData;
  hideModal: () => void;
}) => {
  const mode = data?.mode ?? 'convert';
  const minDurationMs = data?.minDurationMs ?? 3000;
  const [progress, setProgress] = React.useState(0);
  const startedAtRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    logHubxEvent({
      eventName: APP_EVENTS.LND_ANIMATION,
      source: ANALYTICS_SOURCE_BY_MODE[mode],
    });
  }, [mode]);

  React.useEffect(() => {
    if (startedAtRef.current === null) {
      startedAtRef.current = performance.now();
    }
  }, []);

  React.useEffect(() => {
    let timeoutId: number | undefined;

    const advanceProgress = () => {
      setProgress((currentProgress) => {
        if (data?.isComplete) {
          const remaining = 100 - currentProgress;

          if (remaining <= 0) {
            return 100;
          }

          if (remaining <= 5) {
            return 100;
          }

          return (
            currentProgress +
            Math.min(getRandomProgressIncrement(), remaining)
          );
        }

        const startedAt = startedAtRef.current ?? performance.now();
        const elapsed = performance.now() - startedAt;
        const targetProgress = Math.min(
          Math.floor((elapsed / minDurationMs) * 95),
          95
        );
        const remaining = targetProgress - currentProgress;

        if (remaining <= 0) {
          return currentProgress;
        }

        if (remaining <= 5) {
          return targetProgress;
        }

        return (
          currentProgress +
          Math.min(getRandomProgressIncrement(), remaining)
        );
      });
    };

    if (data?.isComplete && progress >= 100) {
      return;
    }

    if (!data?.isComplete && progress >= 95) {
      return;
    }

    timeoutId = window.setTimeout(
      advanceProgress,
      getRandomProgressDelay(Boolean(data?.isComplete))
    );

    return () => {
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [data?.isComplete, minDurationMs, progress]);

  React.useEffect(() => {
    if (data?.isComplete && progress >= 100) {
      hideModal();
    }
  }, [data?.isComplete, hideModal, progress]);

  return (
    <NextUI.Modal
      className="max-w-[348px]"
      classNames={{
        content: 'overflow-visible rounded-[10px] p-6 shadow-none',
      }}
      defaultOpen={true}
      name="EditProcessAnimation"
      backdrop="blur"
      hideCloseButton={true}
      isDismissable={false}
      isKeyboardDismissDisabled={true}
    >
      <div className="flex flex-col items-center">
        <div className="w-full overflow-hidden rounded-[8px]">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="block h-auto w-full"
            src="/videos/pdfgenius-merge-animation.mp4"
          />
        </div>

        <div className="mt-4 flex w-full flex-col items-center gap-2">
          <div className="relative h-[6px] w-full overflow-hidden rounded-[29px] bg-[#F5F5F5]">
            <div
              className="absolute inset-y-0 left-0 rounded-[29px] bg-[#4B7EFF] transition-[width] duration-100 ease-linear"
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="font-barlow text-[16px] font-semibold leading-none text-[#454655]">
            {Math.round(progress)}%
          </span>
        </div>

        <p className="mt-4 font-barlow text-center text-[20px] font-semibold leading-none text-[#454655]">
          {COPY_BY_MODE[mode]}
        </p>
      </div>
    </NextUI.Modal>
  );
};

export default EditProcessAnimationModal;
